import { useEffect, useRef } from "react";
import { openSseStream, SseStreamOptions } from "../utils/sseStream";

export interface UseSseStreamOptions
    extends Pick<SseStreamOptions, "path" | "params" | "listeners"> {
    /** Undefined until the organisation is known; the stream waits for it. */
    organizationId?: string;
    /** Set false to keep the stream closed (default true). */
    enabled?: boolean;
}

/**
 * Declarative wrapper around openSseStream: opens the stream once an
 * organisation is known, closes it on unmount, and reopens it if the
 * organisation changes.
 *
 * Handlers are read through a ref, so a consumer that rebuilds its callbacks
 * every render (the usual case for inline arrow functions) does not tear the
 * stream down and mint a new token on every render.
 */
export function useSseStream({
    path,
    organizationId,
    params,
    listeners,
    enabled = true,
}: UseSseStreamOptions): void {
    const listenersRef = useRef(listeners);
    listenersRef.current = listeners;

    // Event names and params are part of the connection itself, so a change in
    // either must reopen the stream - unlike the handler functions above.
    const eventNames = Object.keys(listeners ?? {})
        .sort()
        .join(",");
    const paramsKey = JSON.stringify(params ?? {});

    useEffect(() => {
        if (!enabled || !organizationId) return;

        const stableListeners = Object.fromEntries(
            eventNames
                .split(",")
                .filter(Boolean)
                .map((name) => [
                    name,
                    (event: MessageEvent) => listenersRef.current?.[name]?.(event),
                ])
        );

        const stream = openSseStream({
            path,
            organizationId,
            params: JSON.parse(paramsKey),
            listeners: stableListeners,
        });

        return () => stream.close();
    }, [path, organizationId, enabled, eventNames, paramsKey]);
}
